import { createAsyncThunk } from '@reduxjs/toolkit';

import type {
  NotificationCategoryId,
  NotificationPreferencesDto,
} from '../../api/notificationTypes';
import { applyAll, applyCategory, applyType } from '../../domain/notifications/notifications';
import { notificationsActions } from '../slices/notificationsSlice';
import { uiActions } from '../slices/uiSlice';
import type { RootState } from '../store';

import { notificationService } from './_deps';

type ThunkConfig = { state: RootState };

export const fetchNotificationPreferences = createAsyncThunk<void, void, ThunkConfig>(
  'notifications/fetch',
  async (_, thunkApi) => {
    thunkApi.dispatch(notificationsActions.setStatus('loading'));
    try {
      const prefs = await notificationService.getPreferences();
      thunkApi.dispatch(notificationsActions.setPreferences(prefs));
      thunkApi.dispatch(notificationsActions.setStatus('idle'));
    } catch (error) {
      thunkApi.dispatch(notificationsActions.setStatus('error'));
      throw error;
    }
  },
);

/**
 * Instant save: show the change right away, then take the server's answer.
 * The answer is applied only when nothing else is in flight, so it cannot overwrite a newer local
 * change; on failure the state is re-read from the server instead of guessing a rollback.
 */
async function saveOptimistically(
  thunkApi: { dispatch: (action: unknown) => unknown; getState: () => RootState },
  key: string,
  optimistic: (prefs: NotificationPreferencesDto) => NotificationPreferencesDto,
  request: () => Promise<NotificationPreferencesDto>,
): Promise<void> {
  const { dispatch, getState } = thunkApi;
  const current = getState().notifications.preferences;
  if (!current) return;

  dispatch(notificationsActions.addPending(key));
  dispatch(notificationsActions.setPreferences(optimistic(current)));
  try {
    const saved = await request();
    dispatch(notificationsActions.removePending(key));
    if (getState().notifications.pending.length === 0) {
      dispatch(notificationsActions.setPreferences(saved));
    }
  } catch (error) {
    dispatch(notificationsActions.removePending(key));
    dispatch(
      uiActions.setNotification({ type: 'error', message: 'Не удалось сохранить настройку' }),
    );
    try {
      dispatch(notificationsActions.setPreferences(await notificationService.getPreferences()));
    } catch {
      // The toast above already told the user; the next page open reloads the state.
    }
    throw error;
  }
}

export const setNotificationType = createAsyncThunk<
  void,
  { type: string; enabled: boolean },
  ThunkConfig
>('notifications/setType', async ({ type, enabled }, thunkApi) => {
  await saveOptimistically(
    thunkApi,
    `type:${type}`,
    (prefs) => applyType(prefs, type, enabled),
    () => notificationService.setType({ type, enabled }),
  );
});

export const setNotificationCategory = createAsyncThunk<
  void,
  { category: NotificationCategoryId; enabled: boolean },
  ThunkConfig
>('notifications/setCategory', async ({ category, enabled }, thunkApi) => {
  await saveOptimistically(
    thunkApi,
    `category:${category}`,
    (prefs) => applyCategory(prefs, category, enabled),
    () => notificationService.setCategory({ category, enabled }),
  );
});

export const setAllNotifications = createAsyncThunk<void, { enabled: boolean }, ThunkConfig>(
  'notifications/setAll',
  async ({ enabled }, thunkApi) => {
    await saveOptimistically(
      thunkApi,
      'all',
      (prefs) => applyAll(prefs, enabled),
      () => notificationService.setAll({ enabled }),
    );
  },
);
